import { autoinject, bindable, computedFrom } from 'aurelia-framework';
import {
	validateTrigger,
	ValidationController,
	ValidationControllerFactory,
	ValidationRules
} from 'aurelia-validation';
import { sortBy } from 'lodash';
import { AccountingDimension, AccountingDimensionService } from './accounting-dimension-api';

@autoinject
export class AccountingDimensions {
	@bindable customerId: string;

	dimensions: AccountingDimension[];
	newDimension = new AccountingDimension();
	loadingDimensions = true;

	private validationController: ValidationController;

	constructor(
		private dimensionService: AccountingDimensionService,
		private validationControllerFactory: ValidationControllerFactory) {

		this.validationController = validationControllerFactory.createForCurrentScope();
		this.validationController.validateTrigger = validateTrigger.manual;
		this.attachValidationRules(this.newDimension);
	}

	async attached() {
		this.dimensions = (await this.dimensionService.getDimensionsForCustomer(this.customerId, true))
			.map(AccountingDimension.fromAM);
		this.dimensions.forEach(d => d.update = this.updateDimension.bind(this));
		this.loadingDimensions = false;
	}

	async updateDimension(dimension: AccountingDimension) {
		this.dimensions = sortBy(this.dimensions, d => d.name);
		return await this.dimensionService.updateDimension(dimension.toAM());
	}

	attachValidationRules(dimension: AccountingDimension) {
		ValidationRules
			.ensure((d: AccountingDimension) => d.name)
				.required()
				.satisfies((name) => this.validateDimensionNameFormat(name))
					.withMessageKey('AccountingDimensions_ValidationError_NameFormat')
				.satisfies((name) => this.validateUniqueDimensionName(name, null))
					.withMessageKey('AccountingDimensions_ValidationError_NameNotUnique')
			.on(dimension);
	}

	async addDimension() {
		const result = await this.validationController.validate({ object: this.newDimension });

		if (!result.valid) {
			return;
		}

		const dimensionAM = await this.dimensionService.addDimension(this.customerId, this.newDimension.toAM());
		this.dimensions = sortBy(this.dimensions.concat(AccountingDimension.fromAM(dimensionAM)), d => d.name);
		this.newDimension = new AccountingDimension();
		this.attachValidationRules(this.newDimension);
	}

	removeDimension(dimension: AccountingDimension) {
		this.dimensionService.removeDimension(dimension);

		this.dimensions = this.dimensions.filter(d => d.name !== dimension.name);
		dimension.confirmRemove = false;
	}

	private dimensionNameRegex: RegExp = /^[a-zäö0-9_+-]+$/i
	validateDimensionNameFormat(newValue: string) {
		return this.dimensionNameRegex.test(newValue);
	}

	validateUniqueDimensionName(newValue: string, oldValue: string) {
		return newValue === oldValue || !this.dimensions.some(v => v.name === newValue);
	}

	validateDimensionName(newValue: string, oldValue: string) {
		return this.validateDimensionNameFormat(newValue) && this.validateUniqueDimensionName(newValue, oldValue);
	}
}
